import { useEffect, useRef, useState } from "react";

export function shouldAnimateBoonDot({ allowed, visible, inView, paused, failed }: {
  allowed: boolean; visible: boolean; inView: boolean; paused: boolean; failed: boolean;
}): boolean {
  return allowed && visible && inView && !paused && !failed;
}

/** A generated motion accent. The ordinary eight-pixel brand dot is always the fallback. */
export function AnimatedBoonDot() {
  const mark = useRef<HTMLSpanElement>(null);
  const [allowed, setAllowed] = useState(false);
  const [visible, setVisible] = useState(true);
  const [inView, setInView] = useState(false);
  const [paused, setPaused] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setAllowed(!preference.matches);
    const updateVisibility = () => setVisible(document.visibilityState === "visible");
    updatePreference();
    updateVisibility();
    preference.addEventListener("change", updatePreference);
    document.addEventListener("visibilitychange", updateVisibility);
    const observer = typeof IntersectionObserver === "function"
      ? new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting))) : null;
    if (observer && mark.current) observer.observe(mark.current);
    else setInView(true);
    return () => {
      observer?.disconnect();
      preference.removeEventListener("change", updatePreference);
      document.removeEventListener("visibilitychange", updateVisibility);
    };
  }, []);

  const playing = shouldAnimateBoonDot({ allowed, visible, inView, paused, failed });
  return (
    <span ref={mark} className="wordmark-mark hero-dot-mark">
      {allowed && !failed && (
        <button type="button" className="hero-dot-control" onClick={() => setPaused((value) => !value)}
          aria-label={paused ? "Play logo animation" : "Pause logo animation"}
          title={paused ? "Play logo animation" : "Pause logo animation"}>
          {playing && <video className="hero-dot-video" autoPlay loop muted playsInline preload="none"
            poster="/favicon.svg" aria-hidden="true" tabIndex={-1} onError={() => setFailed(true)}>
            <source src="/brand/boon-dot-loop.mp4" type="video/mp4" onError={() => setFailed(true)} />
          </video>}
        </button>
      )}
    </span>
  );
}
